import { useState } from 'react';

import { useSetAtom } from 'jotai';
import { v4 as uuid } from 'uuid';

import { smartSearchPlaceholders } from '../../data';
import { useSmartSearchChatContext } from '../../SmartSearchChatContext';
import {
  currentConversationIdAtom,
  pendingFirstMessageAtom,
  upsertConversation,
} from '../../state';
import { RotatingPlaceholderInput } from '../placeholders-and-vanish-input';
import SearchButton, { SearchButtonMode } from '../SearchButton';

import { ModelSelect } from './components';

const TITLE_MAX = 60;

const InputPrompt: React.FC = () => {
  const [input, setInput] = useState('');
  const chat = useSmartSearchChatContext();
  const setCurrentConversationId = useSetAtom(currentConversationIdAtom);
  const setPendingFirstMessage = useSetAtom(pendingFirstMessageAtom);

  const status = chat?.status ?? 'ready';
  const isStreaming = status === 'streaming' || status === 'submitted';
  const conversationActive = chat !== null;

  const canSubmit = input.trim().length > 0 && !isStreaming;

  const buttonMode: SearchButtonMode = isStreaming
    ? 'stop'
    : canSubmit
      ? 'submit'
      : 'disabled';

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const text = input.trim();
    if (!text) return;

    if (!chat) {
      const id = uuid();
      const now = new Date().toISOString();
      upsertConversation({
        id,
        title: text.slice(0, TITLE_MAX),
        createdAt: now,
        updatedAt: now,
        messages: [],
      });
      setPendingFirstMessage(text);
      setCurrentConversationId(id);
      setInput('');
      return;
    }

    if (isStreaming) return;

    chat.sendMessage({ text });
    setInput('');
  };

  const handleStop = () => {
    chat?.stop();
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-base-2 text-variant-body1 border-grayscale-gray2 hover:border-grayscale-gray4 focus-within:border-grayscale-gray4 flex w-full flex-col gap-4 rounded-3xl border p-4 transition-colors duration-300"
    >
      <RotatingPlaceholderInput
        className="outline-none!"
        placeholders={
          conversationActive ? ['Reply...'] : smartSearchPlaceholders
        }
        value={input}
        onChange={e => setInput(e.target.value)}
      />
      <div className="flex items-end justify-between gap-2">
        <div className="flex items-center"></div>
        <div className="flex items-center gap-3">
          <ModelSelect />
          <SearchButton mode={buttonMode} onStop={handleStop} />
        </div>
      </div>
    </form>
  );
};

export default InputPrompt;
